import { useEffect, useId, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FileText, UserRound } from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";

import {
  approveFact,
  draftFact,
  draftValueFor,
  getFact,
  getFactAudit,
  getFactImpact,
  holdsStructuredValue,
  listFacts,
  messageFor,
  ProtexError,
  type Fact,
} from "./api";
import { holdsEveryRecord, type FactRow } from "./catalogue";
import { AlertCard } from "./components/AlertCard";
import { BlastRadiusLine } from "./components/BlastRadiusLine";
import { IngredientsField } from "./components/IngredientsField";
import { ValueDiff } from "./components/ValueDiff";
import {
  decodeRows,
  draftRowsFrom,
  encodeRows,
  ingredientsEditable,
  ingredientsIn,
  ingredientsToValue,
  milligrams,
  sameIngredientList,
  type Ingredient,
  type IngredientDraft,
} from "./ingredients";
import {
  ageOf,
  approvedValuesFor,
  exactDate,
  formatValue,
  marketLabel,
  productLabel,
  scopeChips,
  timeOfDay,
  valueWithUnit,
} from "./format";

/**
 * One fact: what is live, what it would become, what it reaches, and everything
 * that has happened to it.
 *
 * NO PERMISSION BRANCH LIVES HERE (I-7, AGENTS boundary 3). `ShellPage` wraps
 * every module in the shell-owned `PermissionGate`, which makes edit controls
 * genuinely inert for a `view` decision. A module-local `decision === "edit"`
 * branch was a SECOND permission renderer, and two renderers drift.
 *
 * Mounted with `key={row.id}` by the caller, so selecting a different fact gives
 * a FRESH component. Without that, React keeps `nextValue`/`source` across a
 * prop change and fact B inherits fact A's pending correction with Approve
 * already enabled — a correction submitted against the wrong fact.
 */
export function FactDetail({
  row,
  areaName,
  onClose,
}: {
  row: FactRow | undefined;
  areaName: string;
  onClose?: () => void;
}) {
  const client = useQueryClient();
  /**
   * Ids are per-instance, never literals. The tab renders this pane in a column
   * at `lg` and in a sheet below it, and a literal `id` made `<Label htmlFor>`
   * point at whichever copy React wrote first — which on a narrow viewport was
   * the hidden one, so tapping a label focused an invisible input.
   */
  const fieldId = useId();
  const valueFieldId = `${fieldId}-value`;
  const valueHelpId = `${fieldId}-value-help`;
  const sourceFieldId = `${fieldId}-source`;
  const sourceHelpId = `${fieldId}-source-help`;
  /**
   * The SCALAR correction, as typed. For a compound fact it stays empty and
   * `ingredientRows` carries the correction instead — see `nextValue` below,
   * which is the one value the rest of the pane reads either way.
   */
  const [typedValue, setTypedValue] = useState("");
  /**
   * The ingredient list being edited, or `null` for "not touched yet".
   *
   * `null` is load-bearing rather than an empty-list synonym: the editor shows
   * the STORED list from the moment the pane opens, so without a separate
   * untouched state, merely looking at a fact would read as a correction and
   * draft one. Deleting every row is a real edit and gives `[]`, which is
   * refused on save rather than mistaken for "nothing typed".
   */
  const [ingredientRows, setIngredientRows] = useState<IngredientDraft[] | null>(null);
  const [source, setSource] = useState("");
  const [error, setError] = useState<string | null>(null);
  /**
   * That the operator ASKED to publish — not that the dialog is open.
   *
   * The two used to be the same flag, and that is what ate the first click.
   * Clicking Approve straight from the value field blurs it, which starts the
   * draft; the re-render that follows finds no approval target yet, disables
   * the button between mousedown and mouseup, and the click never fires. The
   * operator pressed the only button on the screen and nothing happened.
   *
   * Held as a request and resolved during render (`confirming`, below), the
   * click survives the wait: the dialog opens the moment the draft it needs
   * lands. The safety invariant is untouched — the dialog still cannot open
   * without an approval target, so this can never arm Approve against nothing.
   */
  const [confirmRequested, setConfirmRequested] = useState(false);
  /**
   * The draft this pane created — a NEW fact row with its own id — together with
   * the exact input it was built from.
   *
   * Approval must target the draft, never the row that was selected. Keeping the
   * INPUT beside it is what stops the worst bug this pane could have: type 895,
   * blur (draft saved), then change the field to 950 and approve. Without the
   * input, Approve would publish 895 while the diff on screen said 950.
   *
   * It is also how we know the approval is a self-approval: we watched the same
   * person draft it in this session.
   */
  const [draft, setDraft] = useState<{ fact: Fact; input: string } | null>(null);
  /**
   * The value the field was left holding while a draft was already in flight,
   * waiting for that one to settle.
   *
   * Two drafts must never overlap — they race to resolve, and the loser's
   * record is the one `draft` can be left holding, so Approve would publish a
   * version the screen never showed. The guard that prevents it used to simply
   * SKIP the second draft, and that is what stranded the operator: a field that
   * has already lost focus never blurs again, so a value re-typed during an
   * in-flight save could never get a draft of its own. `approvalTargetFor`
   * matched nothing from then on, and Approve sat enabled and inert under
   * "Saving your correction…" forever — the same dead end as `bf20510` and
   * `0911611`, re-entered through the guard added to close them.
   *
   * Queued rather than dropped. Drafts stay strictly serial AND the value the
   * field was left holding always reaches one.
   */
  const [queuedValue, setQueuedValue] = useState<string | null>(null);
  const [conflict, setConflict] = useState<boolean>(false);
  // State rather than a ref: the confirm dialog's portal container has to be a
  // rendered element by the time the dialog mounts, and only a state update
  // re-renders with it in hand. Same shape as the sheet anchor in `FactsTab`.
  const [dialogAnchor, setDialogAnchor] = useState<HTMLDivElement | null>(null);

  const factId = row?.fact?.fact_id ?? null;

  const fact = useQuery({
    queryKey: ["protex", "fact", factId],
    queryFn: () => getFact(factId as string),
    enabled: factId !== null,
  });

  const audit = useQuery({
    queryKey: ["protex", "audit", factId],
    queryFn: () => getFactAudit(factId as string),
    enabled: factId !== null,
  });

  // A company-wide fact is one key with one approved row per market, so the pane
  // shows the markets side by side: they are corrected one at a time, and seeing
  // £45 / €50 / AED 200 together is what stops one being changed to match another.
  const companyWide = row !== undefined && row.key?.scoped_by_formula === false;

  /**
   * The status of the record this pane is showing, from the freshest read of
   * it that exists yet — the catalogue row paints first, the fact query
   * overwrites it. Both are consulted because the lookup below has to be
   * decided before that fetch settles, and a row that has gone stale in the
   * list must not pin the pane to a stale answer.
   */
  const selectedStatus = fact.data?.data.status ?? row?.fact?.status ?? null;

  /**
   * The record this pane is showing is NOT necessarily the one customers see.
   * A draft, a superseded row and a REJECTED row are all openable straight
   * from the catalogue, and the pane called whichever one it held "Live now" —
   * so a draft of 895 was announced as the published value while approving it
   * said "will become Not set". Nothing on this screen may be labelled live
   * until the approved row for the same key, scope and formula version has
   * been read.
   *
   * Stated as "anything that is not itself approved" rather than as a list of
   * the statuses that need a lookup. The list form was the bug: it named
   * `draft` and `superseded`, so a rejected record fell past it, took the
   * no-lookup path, and announced "No value has ever been published for this"
   * over a live approved sibling. A closed set that must be kept in sync with
   * the API's status union will drift again; the negation cannot.
   *
   * `null` — a registry key with no record at all — needs no lookup: nothing
   * is published for it, and that is not a guess.
   */
  const needsLiveLookup = selectedStatus !== null && selectedStatus !== "approved";
  const siblings = useQuery({
    queryKey: ["protex", "facts", "key", row?.factKey],
    queryFn: () => listFacts({ fact_key: row!.factKey, limit: 200, offset: 0 }),
    enabled: row !== undefined && (companyWide || needsLiveLookup),
  });

  // The record this pane was opened on — approved, draft, superseded or
  // rejected. Resolved here, above the early returns, because the blast-radius
  // query below is a hook and its target depends on it.
  const selectedFact = fact.data?.data ?? row?.fact ?? null;
  const unit = selectedFact?.unit ?? row?.key?.unit ?? null;

  /**
   * WHICH KIND of value this fact holds, and therefore which control authors it.
   *
   * The pane had one control — a text box — and `draftValueFor` coerced
   * whatever was typed into `{ n }` or `{ s }`. For `nutrition.active_
   * ingredients`, which the store holds as `{ items: [...] }`, that meant
   * typing a correction SAVED it as a string under an object key: a shape the
   * store accepts and every downstream reader then misreads. Nothing on screen
   * said so. So the shape is resolved first and the control follows from it
   * (KAL-355, HARD: a compound fact is never authored through a JSON textarea
   * and never through the scalar box either).
   */
  const valueKind = valueKindOf(row, selectedFact);
  /** The stored list, for the editor's starting rows and the diff's left side. */
  const liveIngredients = ingredientsIn(selectedFact?.value) ?? [];
  const editorRows = ingredientRows ?? draftRowsFrom(liveIngredients);

  /**
   * The correction, as the ONE string the whole pane reads.
   *
   * Everything downstream — whether anything has been typed, what a queued
   * draft carries, whether `draft.input` still matches what is on screen — is
   * already written against a single string and is subtle enough that a second
   * parallel state would drift from it. So the ingredient editor ENCODES its
   * rows into the same slot rather than forking the machinery. The encoded form
   * is never rendered and never sent: `buildValue` decodes it and posts the
   * structured envelope.
   */
  //
  // Rows that describe the list the pane OPENED on are "not touched", even when
  // the state is non-null. A re-sort on blur, an edit typed and then reverted,
  // or a renumber of 10, 20 to 1, 2 all leave the same list — and reading that
  // as a correction left a non-empty `nextValue` that `onCommit` rightly never
  // drafts, so Approve waited under "Saving your correction…" with nothing
  // saving. It also hid a teammate's pending draft: rule 3 of
  // `approvalTargetFor` needs an untouched pane.
  const ingredientsTouched =
    ingredientRows !== null && !sameIngredientList(ingredientRows, draftRowsFrom(liveIngredients));
  const nextValue =
    valueKind === "ingredients"
      ? ingredientsTouched && ingredientRows !== null
        ? encodeRows(ingredientRows)
        : ""
      : typedValue;

  // Resolved here, above the early returns, because the draft-queue effect
  // below is a hook and its decision depends on whether the typed value is
  // even a legal one. `row === undefined` short-circuits: there is nothing to
  // build a value against, and that pane renders no field at all.
  const built =
    row === undefined || nextValue === "" ? null : buildValue(row, selectedFact, nextValue, valueKind);
  const typeError = built !== null && "error" in built ? built.error : null;
  const nextLabel = built !== null && "value" in built ? valueWithUnit(built.value, unit) : null;

  /**
   * How the approved-sibling read turned out.
   *
   * A failed read is NOT an answer. Reporting the query's outcome as four
   * states rather than a "settled" boolean is deliberate: the boolean form was
   * written `isSuccess || isError`, which turned a failed lookup into a
   * confident "nothing has ever been published" on the approve surface.
   *
   * `partial` is the same trap one step subtler. The sibling read asks for one
   * page; `total` is the SERVER's count for the key. When the server holds more
   * than we asked for, a page with no approved row in it is not evidence there
   * isn't one — so it may confirm a live value, never deny one.
   */
  const lookupOutcome: LookupOutcome = siblings.isSuccess
    ? holdsEveryRecord(siblings.data.data)
      ? "loaded"
      : "partial"
    : siblings.isError
      ? "failed"
      : "pending";

  const publishedSibling =
    siblings.isSuccess && selectedFact !== null
      ? publishedAmong(siblings.data.data.items, selectedFact)
      : null;

  // What customers see right now, which is a different question from what this
  // pane is showing.
  const live = liveValueFor({
    selected: selectedFact,
    published: publishedSibling,
    lookupNeeded: needsLiveLookup,
    lookupOutcome,
    unit,
  });

  /**
   * The record the "Live now" figure actually came from — never the record the
   * pane was opened on.
   *
   * It is what lets the small line under that figure say "Approved 2 Sep by
   * Damien" about the number CUSTOMERS see. Reading it off `selectedFact`
   * instead would caption the live value with the provenance of a draft — a
   * sentence describing one record printed under a different record's number,
   * which is the same class of error as calling a draft the live value.
   *
   * `null` whenever the live value is not a published one: nothing approved a
   * blank, and nothing approved a read that failed.
   */
  const liveRecord =
    selectedFact !== null && selectedFact.status === "approved" ? selectedFact : publishedSibling;

  /**
   * The two sides of the diff as LINES, for a compound value.
   *
   * An ingredient list is a real correction with a real thing to check, but it
   * is not a figure: set at the card's 26px it fills the pane and still cannot
   * be compared ingredient by ingredient. As lines the two columns stack in
   * step, so a changed amount sits directly across from the one it replaces.
   *
   * `null` for every scalar — the figure is right for those, and this must not
   * quietly restyle the number the whole surface is built around.
   */
  const liveLines = valueKind === "ingredients" ? asLines(liveRecord?.value) : null;
  const nextLines =
    valueKind === "ingredients" && built !== null && "value" in built ? asLines(built.value) : null;

  /**
   * The blast radius of the value being CORRECTED — the surfaces the LIVE value
   * reaches are exactly the ones the correction will change.
   *
   * Keyed on the live record, never on the record the pane happens to be open
   * on. Those diverge on the "pick up someone else's draft" path: the draft is
   * published nowhere, so asking about IT returned zero surfaces and the pane
   * said "This is not published anywhere yet" while Approve was about to
   * overwrite every panel its approved sibling reaches — KET-3853's own failure
   * mode, restated by the screen that exists to prevent it.
   *
   * `null` while the live record is not known: an unread reach is reported as
   * unread (below), never as a count of zero.
   */
  const impactTarget = impactTargetFor({
    live,
    selected: selectedFact,
    published: publishedSibling,
  });
  const impact = useQuery({
    queryKey: ["protex", "impact", impactTarget],
    queryFn: () => getFactImpact(impactTarget as string),
    enabled: impactTarget !== null,
    retry: false,
  });

  /**
   * What the reach line is allowed to claim.
   *
   * Three separate claims, and the first is the only one that may show a count:
   *   - the live record is known  → the impact query's own state
   *   - it is still being read    → "checking"
   *   - the read failed or was capped → "could not confirm"
   *
   * `firstValue` is `live.state === "none"` — the deliberate "nothing has ever
   * been published" answer — and not `row.fact === null`, which was true of a
   * registry key with no record but ALSO false for a draft whose approved
   * sibling could not be read.
   */
  const impactUnknown = live.state === "unknown";
  const impactPending = impactUnknown
    ? lookupOutcome === "pending"
    : impactTarget !== null && impact.isLoading;
  const impactFailed = impactUnknown ? lookupOutcome !== "pending" : impact.isError;
  const impactFirstValue = live.state === "none";

  /**
   * The typed value travels WITH the mutation, as its variable.
   *
   * It used to be held in a ref that `onSuccess` read when the response landed,
   * which is a different moment from the one the request was built in. Two
   * overlapping blurs — 895 saved, then 950 — made those moments disagree: both
   * handlers read the ref's latest "950" and paired it with whichever record
   * came back, so `draft` could end up as v3-for-895 labelled `input: "950"`.
   * That label is exactly what `approvalTargetFor` matches on, so Approve armed
   * against the OLDER draft while the diff and the confirm dialog both read 950
   * — publishing a number nobody was shown.
   *
   * React Query hands the variables back to `onSuccess` unchanged, so the
   * record and the value that produced it can no longer come apart. The blur
   * handler additionally refuses to start a second draft while one is in
   * flight, so the two never overlap in the first place.
   */
  const draftMutation = useMutation({
    mutationFn: (input: string) => {
      const built = buildValue(row!, selectedFact, input, valueKind);
      if ("error" in built) return Promise.reject(new TypeError(built.error));
      return draftFact({
        fact_key: row!.factKey,
        scope: row!.fact?.scope ?? { region: "*", store: "*", channel: "*", locale: "*" },
        formula_version_id: row!.formulaVersionId,
        value: built.value,
        unit: row!.fact?.unit ?? row!.key?.unit ?? null,
        authority_grade: "human_verified",
      });
    },
    onSuccess: (created, input) => {
      setError(null);
      setDraft({ fact: created.data, input });
      void client.invalidateQueries({ queryKey: ["protex"] });
    },
    onError: (err) => {
      // The request the operator made was for THIS draft. Leaving it armed
      // would pop the confirm dialog unbidden on whatever draft happened to
      // succeed next.
      setConfirmRequested(false);
      setError(messageFor(err, areaName));
    },
  });

  const approveMutation = useMutation({
    mutationFn: (targetId: string) => approveFact(targetId, source),
    onSuccess: () => {
      setError(null);
      setConfirmRequested(false);
      setDraft(null);
      setTypedValue("");
      // Back to "not touched": the editor re-seeds from the record that was
      // just published, so the rows on screen are the rows now live rather
      // than the ones that were being corrected a moment ago.
      setIngredientRows(null);
      // The source is cleared with the value it justified. It was left behind,
      // so correcting a second fact in the same pane arrived with the FIRST
      // correction's source already filled in and the button already enabled —
      // and an approval carries its source onto the audit trail forever. A
      // stale one there is worse than a blank: it is a retrace that leads
      // somewhere plausible and wrong. The point of the field is that someone
      // checked THIS number.
      setSource("");
      // A queued draft belongs to the correction that has just been published.
      // Carrying it past the publish is the same class of leftover as the
      // source above: it would draft a value for the NEXT fact opened in this
      // pane.
      setQueuedValue(null);
      // The stale conflict this clears is the one from a PREVIOUS attempt.
      // Without it the red "someone approved a different correction first"
      // card outlived the conflict, sitting over a publish that had just
      // succeeded.
      setConflict(false);
      void client.invalidateQueries({ queryKey: ["protex"] });
    },
    onError: (err) => {
      setConfirmRequested(false);
      // A 409 is not a generic error: someone approved a different correction
      // first. The typed value stays in the box so the work is not lost.
      if (err instanceof ProtexError && err.status === 409) setConflict(true);
      else setError(messageFor(err, areaName));
    },
  });

  /**
   * Drains the queued value the moment the in-flight draft settles.
   *
   * This is the half that makes queuing worth anything: without it the value
   * would sit in `queuedValue` as surely as it used to sit nowhere at all.
   * `queuedDraftIntent` holds the rules — it is pure, so they are tested
   * directly rather than through a blur nobody can simulate here.
   */
  useEffect(() => {
    const intent = queuedDraftIntent({
      queued: queuedValue,
      value: nextValue,
      typeError,
      draftedInput: draft?.input ?? null,
      inFlight: draftMutation.isPending,
    });
    if (intent === "hold") return;
    // Cleared before the mutation either way: a queue entry is one request's
    // worth of intent, and leaving it behind would replay it on every render.
    setQueuedValue(null);
    if (intent === "start" && queuedValue !== null) draftMutation.mutate(queuedValue);
  }, [queuedValue, nextValue, typeError, draft, draftMutation]);

  if (row === undefined) {
    return (
      <p className="text-sm text-ks-ink">
        This fact is no longer in the list. It may have been superseded — reopen it from the
        catalogue.
      </p>
    );
  }

  // Never over work in progress. `factId` comes off the row, and the row under
  // this pane CHANGES identity the moment a first value is drafted: the
  // synthesised Missing row (which has no record, so no `factId`) is replaced
  // by the draft it created (which has one), starting a fetch. Blanking to a
  // skeleton there would take the typed value, the source and Approve off the
  // screen in the middle of the correction that caused it — the same loss as
  // the "no longer in the list" message, one commit later.
  if (factId !== null && fact.isLoading && draft === null && nextValue === "") {
    return (
      <div className="space-y-3" aria-busy>
        <Skeleton className="h-5 w-56" />
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  // 404 says the fact is gone — never a blank pane.
  if (factId !== null && fact.isError) {
    return (
      <Alert variant="destructive">
        <AlertTitle>{messageFor(fact.error, areaName)}</AlertTitle>
        {onClose && (
          <AlertDescription>
            {/* Navigation, not an edit — and the only way out of an error
                pane, so locking it would strand a reader on a dead end. */}
            <Button variant="outline" size="sm" className="mt-2" data-nonmutating onClick={onClose}>
              Back to the list
            </Button>
          </AlertDescription>
        )}
      </Alert>
    );
  }

  const nothingPublished = live.state === "none";

  /**
   * SOMEONE ELSE's draft — a pending correction this pane did not create.
   *
   * The pane lands on its OWN draft too: a first value drafted against a
   * Missing row swaps that row for the record it just created. Telling the
   * operator "someone drafted 895" about the number they typed a second ago
   * reads as a second, competing correction, so the two are separated here
   * once and every consumer below reads the same answer.
   */
  const othersDraft =
    selectedFact !== null &&
    selectedFact.status === "draft" &&
    selectedFact.fact_id !== draft?.fact.fact_id
      ? selectedFact
      : null;
  /** …as a label, and only while the operator has not typed over it. */
  const showingOthersDraft = othersDraft ? valueWithUnit(othersDraft.value, unit) : null;

  /** The small line under the correction: this pane's own draft, once stored. */
  const draftNote =
    draft && draft.input === nextValue
      ? `Draft saved ${timeOfDay(draft.fact.approved_at ?? new Date().toISOString())} · not live yet`
      : null;

  const approvalTarget = approvalTargetFor({
    draft,
    nextValue,
    selectedId: selectedFact?.fact_id ?? null,
    selectedStatus: selectedFact?.status ?? null,
  });
  // A draft that FAILED is not a draft still saving. Read straight off the
  // mutation: it resets on the next `mutate`, so the flag clears the moment a
  // retry starts and never outlives the failure it describes.
  const draftFailed = draftMutation.isError;
  const blocked = approveBlockedBecause({
    typeError,
    hasTarget: approvalTarget !== null,
    hasValue: nextValue !== "",
    draftFailed,
    source,
    valueKind,
  });

  /**
   * Waiting on the draft is a DELAY, not a refusal.
   *
   * It is the one blocking reason the operator can do nothing about, and the
   * only one that arrives mid-click — so the button stays pressable through it
   * and the request queues, while every other reason (no value, a value that is
   * not a number, no source) still disables it outright.
   */
  const awaitingDraft =
    approvalTarget === null &&
    nextValue !== "" &&
    typeError === null &&
    !draftFailed &&
    source.trim() !== "";
  // The dialog opens only when NOTHING blocks — which subsumes "there is an
  // approval target", since a missing target is itself a blocking reason. The
  // request alone can never open it.
  const confirming = confirmRequested && blocked === null;

  /**
   * Whether this pane offers a source field and an Approve button at all.
   *
   * Every authorable fact does. A STRUCTURED one does only while there is a
   * record for the button to act on — a draft already waiting. Without that
   * there is nothing to publish and nothing to justify, and an Approve under a
   * read-only notice would be the pane contradicting itself.
   */
  const canResolve = valueKind !== "structured" || approvalTarget !== null;

  return (
    <div className="space-y-6">
      {/* The confirm dialog's portal target, and the reason it exists.

          Radix mounts a portal at `document.body` by default, which puts the
          dialog OUTSIDE the shell's `PermissionGate` — a wrapper that makes
          edit controls inert with a descendant selector over its own subtree.
          The sheet fix in `FactsTab` closed that escape for the pane; it does
          nothing for a dialog the pane itself opens, which portals to the body
          from wherever the pane happens to be mounted. So the pane could be
          correctly inert while "Publish this correction" — the button that
          actually writes — sat live on the same screen.

          Anchoring the portal here keeps the dialog a DOM descendant of the
          gate in BOTH layouts (docked pane and narrow sheet), so one permission
          renderer still covers everything and the module holds no permission
          branch of its own (I-7, AGENTS boundary 3). The dialog stays
          `position: fixed`, so it is laid out against the viewport exactly as
          before — nothing between the gate and here creates a containing block.

          `contents` so the anchor generates no box: an empty child would
          otherwise take a `space-y-6` band of its own. */}
      <div ref={setDialogAnchor} className="contents" />

      <FactHeader row={row} fact={selectedFact} companyWide={companyWide} />

      {conflict ? (
        <ConflictCard
          typed={nextLabel}
          onReload={() => {
            setConflict(false);
            setDraft(null);
            void fact.refetch();
            void client.invalidateQueries({ queryKey: ["protex"] });
            // The card promises the typed value is "still in the box below —
            // nothing was lost", and clearing the draft it was approved
            // against is what made that promise empty: no target, a non-empty
            // field and no failure to report is the same shape as a save still
            // running, so Approve went back under "Saving your correction…"
            // with nothing saving and no field left to blur. That is the dead
            // end `0911611` closed, re-opened by the one button offered for
            // getting out of a conflict.
            //
            // So the value is re-drafted here, against the state that has just
            // been reloaded — the same thing leaving the field does, for a
            // draft the conflict has invalidated. `draftedInput: null` because
            // the draft cleared a line above has not reached this render yet.
            const intent = draftIntentFor({
              value: nextValue,
              typeError,
              draftedInput: null,
              inFlight: draftMutation.isPending,
            });
            if (intent === "start") draftMutation.mutate(nextValue);
            else if (intent === "queue") setQueuedValue(nextValue);
          }}
        />
      ) : null}

      {/* The selected record, when it is not the live one, gets its own line —
          it is the number Approve publishes, so leaving it implicit is what
          produced a confirm dialog that named the wrong value.

          A CARD rather than a sentence, because of what it has to stop: the
          reader assuming the number in front of them is the one customers see.
          It says both halves — what is drafted, and what is still live — in the
          one place, in the brand tint that means "something is pending here".

          It LEADS the pane body, above the numbers, as the mock-up orders it:
          "a draft is waiting" is the reason this fact is open, and under the
          diff card it read as a footnote to a comparison the reader had already
          made (outcome-audit cycle 3, M6). */}
      {othersDraft !== null && (
        <div className="flex gap-2.5 rounded-ks-lg border border-ks-purple-3 bg-ks-purple-4 px-3.5 py-3">
          <FileText aria-hidden className="mt-px size-[18px] shrink-0 text-ks-purple" />
          <p className="text-sm leading-snug text-ks-ink">
            <strong>Someone drafted {valueWithUnit(othersDraft.value, unit)}.</strong> Customers
            still see {liveLabelFor(live, lookupOutcome)} until someone approves it.
          </p>
        </div>
      )}
      {nothingPublished ? (
        <>
          {/* Ruled red, as the mock-up draws it and as S1's Missing pill already
              is: a value that has never existed is the one state on this pane
              that is wrong on a shelf right now. */}
          <Alert className="border-ks-danger">
            <AlertTitle>No value has ever been published for this.</AlertTitle>
            <AlertDescription>
              The {row.title.toLowerCase()} line is blank on every {row.product} panel until someone
              approves a number.
            </AlertDescription>
          </Alert>
          {/* The alert used to REPLACE the diff, which took the "Your
              correction: 950 g" echo off the pane for the whole of Flow C —
              the one flow where the typed number has never been checked
              against anything, so the guard of C3 interaction rule 2 matters
              most. It survived only inside the confirm dialog, which is one
              click too late to catch a typo. `Not set` is the honest left-hand
              side: the blank the alert has just described, stated in the same
              shape as every other correction on this surface.

              Only once something is typed — an empty diff here would just be
              the alert's own sentence a second time, in bigger type. */}
          {nextLabel !== null && (
            <ValueDiff
              current={valueWithUnit(null, unit)}
              next={nextLabel}
              nextLines={nextLines}
              unit={unit}
              currentCaption="Live now"
              nextNote={draftNote}
            />
          )}
        </>
      ) : (
        <ValueDiff
          current={liveLabelFor(live, lookupOutcome)}
          // Someone else's draft fills the right-hand side until the operator
          // types over it: it is the number Approve would publish, and an empty
          // right column beside a live value said the opposite — that nothing
          // was pending on a fact whose whole reason for being on screen is
          // that something is.
          next={nextLabel ?? showingOthersDraft}
          currentLines={liveLines}
          nextLines={
            nextLines ?? (nextLabel === null && othersDraft !== null ? asLines(othersDraft.value) : null)
          }
          unit={unit}
          // A company-wide fact has one live value PER MARKET, listed below.
          // An uncaptioned "Live now €50" above that list read as the fact's
          // single value — the thing the mock-up's markets-side-by-side state
          // exists to rule out — so the card names the market it corrects.
          currentCaption={companyWide ? `Live now · ${row.market}` : "Live now"}
          // Whose number the live figure is, under the live figure — never
          // under the correction, and never read off the selected record.
          currentNote={liveRecord ? provenance(liveRecord) : null}
          // Named as THEIRS rather than as "your correction" — the operator has
          // not corrected anything yet, and captioning a stranger's number that
          // way is the pane telling them they did something they did not.
          nextCaption={nextLabel === null && showingOthersDraft !== null ? "Drafted" : "Your correction"}
          nextNote={
            nextLabel === null && showingOthersDraft !== null ? "Not live yet" : draftNote
          }
        />
      )}

      {selectedFact && selectedFact.status === "superseded" && (
        <p className="text-sm text-ks-grey-1">
          This is an older version holding <strong>{valueWithUnit(selectedFact.value, unit)}</strong>{" "}
          — it is not what customers see.
        </p>
      )}
      {/* A rejected record is openable from the catalogue like any other, and
          without this line it renders as a bare number with no hint that it was
          turned down — beside a "Live now" value it is not, and never was. */}
      {selectedFact && selectedFact.status === "rejected" && (
        <p className="text-sm text-ks-grey-1">
          This correction was rejected, holding{" "}
          <strong>{valueWithUnit(selectedFact.value, unit)}</strong> — it was never published.
        </p>
      )}
      {/* The SELECTED record's own provenance, and only when it is not the
          live one: the live record's is already the small line under the "Live
          now" figure, and printing the same sentence twice under two different
          numbers is how a reader ends up attributing one to the other. */}
      {/* …and not for someone else's draft either: the card above already says
          "drafted, not live, customers still see X" with the actual live value
          in it, so `provenance` would restate it a weaker way, one line down. */}
      {selectedFact !== null && selectedFact !== liveRecord && othersDraft === null && (
        <p className="text-sm text-ks-grey-1">{provenance(selectedFact)}</p>
      )}

      {companyWide && <MarketStack row={row} query={siblings} areaName={areaName} />}

      <div className="space-y-4">
        {/* Leaving the correction SAVES the draft — for the text box that is a
            blur, and for the ingredient list it is any row change. The old
            build made saving a second button, so the operator pressed two
            things to do one thing, and a draft nobody saved was silently
            discarded on navigation.

            Re-drafts only when the value has CHANGED since the last draft,
            otherwise a second edit would leave the first draft as the thing
            Approve publishes. Never while one is already in flight, and never
            DROPPED for that reason either: the value queues and the effect
            above drafts it the moment the in-flight one settles. Skipping was
            not safe — a field that has already lost focus never blurs again,
            and Approve was stranded enabled and inert for the rest of the
            session. Named once here because two controls now share it. */}
        {(() => {
          const commit = (value: string) => {
            const intent = draftIntentFor({
              value,
              // Judged against the value being COMMITTED, never against the
              // render's own `typeError`. `IngredientsField` calls `onChange`
              // and `onCommit` in one synchronous handler (a reorder, a delete,
              // a settled amount), so this closure runs on the PRE-update
              // render, where `typeError` still describes the list the operator
              // has just FIXED. `draftIntentFor` short-circuits to "skip" on a
              // non-null error, so the one edit that got silently dropped was
              // the edit that resolved the error: the message vanished on the
              // next render, no draft had been queued, `awaitingDraft` stayed
              // true, and Approve sat under "Saving your correction…" with
              // nothing in flight and no blur left to retry it. On a screen
              // whose job is correcting customer-facing numbers, a dropped save
              // is the worst answer available.
              typeError: typeErrorFor(row, selectedFact, value, valueKind),
              draftedInput: draft?.input ?? null,
              inFlight: draftMutation.isPending,
            });
            if (intent === "start") draftMutation.mutate(value);
            else if (intent === "queue") setQueuedValue(value);
          };
          const help = draftMutation.isPending
            ? "Saving…"
            : (draftNote ??
              "Leaving this field saves a draft. Nothing is published until you approve.");

          if (valueKind === "ingredients") {
            return (
              <IngredientsField
                id={valueFieldId}
                helpId={valueHelpId}
                label={nothingPublished ? "First ingredient list" : "Ingredients"}
                rows={editorRows}
                error={typeError}
                help={
                  draftMutation.isPending
                    ? "Saving…"
                    : (draftNote ??
                      "Each change saves a draft. Nothing is published until you approve.")
                }
                onChange={(next) => {
                  // A request to publish was a request to publish THAT list.
                  // Changing a row withdraws it, so the dialog cannot open
                  // later on a list the operator has since moved past.
                  setConfirmRequested(false);
                  setIngredientRows(next);
                }}
                onCommit={(rows) => {
                  // Tabbing through the fields is not a correction. The editor
                  // is SEEDED with the list the pane opened on, so every blur
                  // arrives holding a full set of rows — without this, moving
                  // focus across the pane would draft an identical copy and
                  // leave a pending version in the audit trail that nobody
                  // asked for. The SAME test `nextValue` uses, so a list this
                  // skips is always one the pane also reads as untouched, and
                  // Approve is never left waiting for a draft that will not come.
                  if (sameIngredientList(rows, draftRowsFrom(liveIngredients))) return;
                  commit(encodeRows(rows));
                }}
              />
            );
          }

          if (valueKind === "structured") {
            // Never a JSON textarea. The store's own encoding is not a control
            // a schema-illiterate reader can operate, and a stray brace here
            // publishes a malformed panel. Saying plainly that this screen
            // cannot author the value — and who can change that — is the
            // honest state; a box that accepts anything is not.
            //
            // Two different reasons land here and they are not the same
            // sentence. An ingredient key whose stored value this pane could
            // not READ is not a shape we have no editor for — we have exactly
            // that editor, and the value in the store does not fit it. Saying
            // "no editor yet" there would send the reader to ask for a feature
            // that already exists, instead of at the record that is wrong.
            //
            // Both go through `AlertCard`, which is the surface's one card
            // shape, and they take DIFFERENT tones from it because they are not
            // the same event. A list this pane could not read is a refusal —
            // red rule and a circle mark, the shape the 409 card and the S1
            // load failure already carry, so the reader learns one thing from
            // all three. A structured value with no editor yet is no alarm at
            // all: it takes the quiet tone, a grey rule and no mark. Written as
            // bare `Alert`s these were the two halves of the same mistake —
            // the refusal took a red rule without the mark, and the notice
            // inherited `currentColor` and drew the loudest rule on the pane
            // (outcome-audit cycle 6, O2 and O1).
            return isIngredientKey(row) ? (
              <AlertCard
                icon="circle"
                title="This ingredient list could not be read."
                detail={
                  <>
                    {row.title} is stored in a shape this screen does not recognise, so it is shown
                    above exactly as it is held and cannot be edited here — editing it would replace
                    whatever is actually stored with a list built from an empty editor. Ask the{" "}
                    {areaName} area owner to repair the record.
                  </>
                }
              />
            ) : (
              <AlertCard
                tone="quiet"
                title="This value is a structured one, and is read-only here."
                detail={
                  <>
                    {row.title} is stored as a set of parts rather than a single number, and this
                    screen has no editor for its shape yet. It is shown above exactly as it is
                    published. Ask the {areaName} area owner to change it.
                  </>
                }
              />
            );
          }

          return (
            <ValueField
              id={valueFieldId}
              helpId={valueHelpId}
              label={nothingPublished ? "First value" : "New value"}
              unit={unit}
              numeric={valueKind === "number"}
              value={nextValue}
              error={typeError}
              help={help}
              onChange={(next) => {
                // A request to publish was a request to publish THAT number.
                // Typing a different one withdraws it, so the dialog cannot
                // open later on a value the operator has since moved past.
                setConfirmRequested(false);
                setTypedValue(next);
              }}
              onBlur={() => commit(nextValue)}
            />
          );
        })()}

        {/* The source and the Approve button belong to a CORRECTION. A value
            this screen cannot author has none to justify or publish, so
            offering both under a notice that says it is read-only here would
            be the pane contradicting itself — and a disabled Approve reads as
            "you are not allowed", which is a different and untrue claim.
            The reach line stays: how far the live value carries is worth
            knowing whether or not you can change it.

            …unless a DRAFT is already pending on it. Approving one is a review,
            not an edit: the value was written elsewhere, this pane publishes
            the record as it stands, and no editor is involved. Hidden outright,
            a structured draft could be resolved from nowhere — not here, and
            not from the catalogue or the audit trail, neither of which offers
            an approve action — so it sat pending for good, holding the live
            value behind it. "Read-only here" is a statement about authoring a
            NEW value; it was never a reason to strand one somebody already
            wrote. */}
        {canResolve && (
          <SourceField
            id={sourceFieldId}
            helpId={sourceHelpId}
            value={source}
            onChange={setSource}
          />
        )}

        <BlastRadiusLine
          impact={impact.data?.data}
          pending={impactPending}
          failed={impactFailed}
          firstValue={impactFirstValue}
        />

        {canResolve && (
          <ApproveAction
            // Picking up a teammate's draft is a REVIEW: the operator has typed
            // nothing, and the button publishes a number someone else wrote.
            // Saying so is what the mock-up's "Review and approve" is for. A
            // structured fact is ALWAYS that case — this pane cannot author one,
            // so the only thing the button can ever publish is a draft someone
            // else wrote.
            label={
              valueKind === "structured" || (othersDraft !== null && nextValue === "")
                ? "Review and approve"
                : undefined
            }
            onClick={() => setConfirmRequested(true)}
            disabled={(blocked !== null && !awaitingDraft) || approveMutation.isPending}
            blocked={blocked}
          />
        )}
      </div>

      {error && (
        <p role="alert" className="text-sm font-bold text-ks-danger">
          {error}
        </p>
      )}

      <History
        areaName={areaName}
        query={audit}
        fact={fact.data?.data ?? row?.fact ?? null}
        unit={unit}
        enabled={factId !== null}
      />

      {/* Rendered only once the anchor is on screen: mounting the dialog before
          its container exists would fall back to the body portal, which is the
          escape this anchor is here to prevent. `confirming` lives on this
          component, so nothing is lost by waiting a commit. */}
      {approvalTarget && dialogAnchor !== null && (
        <ApproveDialog
          container={dialogAnchor}
          open={confirming}
          onOpenChange={setConfirmRequested}
          title={row.title}
          product={
            companyWide ? "Every product" : selectedFact ? productLabel(selectedFact) : row.product
          }
          market={selectedFact ? marketLabel(selectedFact.scope) : row.market}
          unit={unit}
          currentLabel={liveLabelFor(live, lookupOutcome)}
          // What Approve will ACTUALLY publish — read off the approval target,
          // never off this pane's own draft state. The two diverge on the
          // "pick up someone else's draft" path, where the pane holds no draft
          // of its own and the old fallback resolved to "Not set" while a real
          // number was published.
          nextLabel={publishLabelFor({ approvalTarget, draft, selected: selectedFact, typedLabel: nextLabel, unit })}
          currentLines={liveLines}
          // The list Approve will publish, read off the same record the label
          // is: two sides of one dialog built from two different records is
          // exactly how a confirm step ends up naming a value nobody approved.
          nextLines={
            nextLines ??
            asLines(
              approvalTarget !== null && approvalTarget === draft?.fact.fact_id
                ? draft.fact.value
                : selectedFact?.status === "draft"
                  ? selectedFact.value
                  : undefined,
            )
          }
          source={source}
          impact={impact.data?.data}
          impactPending={impactPending}
          impactFailed={impactFailed}
          impactFirstValue={impactFirstValue}
          // Whether the DRAFT BEING APPROVED is this pane's own — not merely
          // whether this pane holds one. See `selfApprovedBy`.
          selfApproved={selfApprovedBy({ approvalTarget, draft })}
          pending={approveMutation.isPending}
          onConfirm={() => approveMutation.mutate(approvalTarget)}
        />
      )}
    </div>
  );
}

/**
 * What leaving the value field should do about the draft.
 *
 * Three answers, and the middle one is the whole point:
 *   - `start` — nothing is in flight, so save this value now.
 *   - `queue` — a draft is already in flight. Two of them race, and the loser's
 *     record is the one the pane can be left holding, so the second must wait
 *     rather than run. It must NOT simply be dropped: a field that has lost
 *     focus never blurs again, so a dropped value has no second chance and
 *     Approve can never find a target for it.
 *   - `skip` — there is nothing worth saving: an empty field, a value that is
 *     not the right type, or — with nothing in flight — a value the draft in
 *     hand was already built from. That last test is asked only AFTER the
 *     in-flight one: while a save is running, the draft in hand is about to be
 *     replaced, so matching it proves nothing about what will be there.
 */
export type DraftIntent = "start" | "queue" | "skip";

export function draftIntentFor({
  value,
  typeError,
  draftedInput,
  inFlight,
}: {
  value: string;
  typeError: string | null;
  /** The input the draft in hand was built from — `null` when there is none. */
  draftedInput: string | null;
  inFlight: boolean;
}): DraftIntent {
  if (value === "" || typeError !== null) return "skip";
  // BEFORE the "already drafted" test, never after it. The draft in hand is
  // NOT the draft in flight: type 895, blur (895 saved), type 950, blur (950
  // now in flight, `draft` still says 895), then re-type 895 and blur. Asking
  // first whether 895 was already drafted answered yes — off a record the
  // in-flight save was about to replace — so the re-typed value was silently
  // dropped. The 950 draft then landed, `draft.input` no longer matched the
  // field, `approvalTargetFor` matched nothing, and Approve sat enabled and
  // inert under "Saving your correction…" with no field left to blur: the
  // exact dead end `bf78da1` queued the second draft to close, re-entered
  // through the guard's own ordering.
  //
  // Queueing it costs nothing when it turns out to be redundant —
  // `queuedDraftIntent` re-asks this question once the flight settles, against
  // the draft that ACTUALLY landed, and drops the value if that draft already
  // holds it.
  if (inFlight) return "queue";
  if (draftedInput === value) return "skip";
  return "start";
}

/**
 * What to do with a queued value once the draft it was waiting behind settles.
 *
 * `drop` rather than `start` when the field has moved on since: a value the
 * operator has typed past does not deserve a record in the audit trail, and
 * leaving the field again will draft whatever it holds then. `drop` also covers
 * the case where the draft that just landed turns out to have been for this
 * very value — a second identical draft row is a real record of nothing.
 */
export function queuedDraftIntent({
  queued,
  value,
  typeError,
  draftedInput,
  inFlight,
}: {
  queued: string | null;
  /** What the field holds NOW. */
  value: string;
  typeError: string | null;
  draftedInput: string | null;
  inFlight: boolean;
}): "hold" | "start" | "drop" {
  if (queued === null || inFlight) return "hold";
  if (queued !== value) return "drop";
  return draftIntentFor({ value: queued, typeError, draftedInput, inFlight: false }) === "start"
    ? "start"
    : "drop";
}

/**
 * Which record Approve actually publishes — the single most dangerous decision
 * on this surface.
 *
 * Three rules, and the second is the one that bites:
 *   1. The draft this pane created is the target.
 *   2. ONLY while the field still holds the value that draft was built from.
 *      Type 895, blur (draft saved), change the field to 950, approve — without
 *      this check the button publishes 895 while the diff on screen says 950.
 *   3. Otherwise, an already-draft row that was selected and NOT typed over —
 *      the "pick up someone else's draft" path.
 *
 * Anything else is null. Approving an `approved` row is a no-op at best and a
 * re-approval of the old value at worst.
 */
export function approvalTargetFor({
  draft,
  nextValue,
  selectedId,
  selectedStatus,
}: {
  draft: { fact: { fact_id: string }; input: string } | null;
  nextValue: string;
  /** The record the pane was OPENED on — not necessarily the live one. */
  selectedId: string | null;
  selectedStatus: string | null;
}): string | null {
  if (draft !== null && draft.input === nextValue) return draft.fact.fact_id;
  if (nextValue === "" && selectedId !== null && selectedStatus === "draft") return selectedId;
  return null;
}

/**
 * The value the Approve button will ACTUALLY publish, as the confirm dialog
 * must state it.
 *
 * Read off the approval target, never off the pane's own draft state. The two
 * diverge on rule 3 of `approvalTargetFor` — the "pick up someone else's draft"
 * path — where this pane created no draft and typed nothing, so a fallback
 * through `draft?.fact.value` resolved to "Not set" while a real number went
 * live. The dialog exists to state what is about to happen; stating it wrongly
 * is worse than not showing it.
 */
export function publishLabelFor({
  approvalTarget,
  draft,
  selected,
  typedLabel,
  unit,
}: {
  approvalTarget: string | null;
  draft: { fact: Pick<Fact, "fact_id" | "value">; input: string } | null;
  selected: Pick<Fact, "fact_id" | "value"> | null;
  typedLabel: string | null;
  unit: string | null;
}): string {
  if (draft !== null && approvalTarget === draft.fact.fact_id) {
    return typedLabel ?? valueWithUnit(draft.fact.value, unit);
  }
  if (selected !== null && approvalTarget === selected.fact_id) {
    return valueWithUnit(selected.value, unit);
  }
  return "Not set";
}

/**
 * Is the person approving the person who drafted the value being approved?
 *
 * The same guard `publishLabelFor` uses, and for the same reason: holding a
 * draft in this pane is NOT the same as that draft being the approval target.
 * They diverge on rule 3 of `approvalTargetFor` — pick up a stranger's 895,
 * type 950 and blur, then clear the field, and the target falls back to the
 * STRANGER's draft while this pane still holds one of its own.
 *
 * Asking `draft !== null` there printed "You drafted this and you are approving
 * it — Self-approved will be recorded on the audit trail" over someone else's
 * number. That is a false statement about what the audit trail is going to say,
 * on the one dialog whose entire job is to state what is about to happen, and
 * it points the reader's separation-of-duties check at the wrong person.
 */
export function selfApprovedBy({
  approvalTarget,
  draft,
}: {
  approvalTarget: string | null;
  draft: { fact: Pick<Fact, "fact_id"> } | null;
}): boolean {
  return draft !== null && approvalTarget === draft.fact.fact_id;
}

/** What the reader is told when the live value could not be read at all. */
export const LIVE_UNREADABLE = "Could not be read";

/** ...and while the read that would answer is still in flight. */
export const LIVE_CHECKING = "Checking…";

/**
 * The "Live now" caption, in the reader's words.
 *
 * `liveValueFor` collapses "still reading" and "read and failed" into one
 * `unknown`, which is right for a DECISION — neither licenses a claim about the
 * storefront — and wrong for a LABEL. Printing "Could not be read" over a
 * request that is still in flight states a failure that has not happened, on
 * the first paint of every draft, superseded and rejected record. The reader
 * then watches it flip to a number, which teaches them the line is unreliable
 * exactly where it most needs to be believed.
 *
 * `partial` reads as failed rather than as pending: the request finished, and
 * what it settled on is that we still do not know.
 */
export function liveLabelFor(live: LiveValue, lookupOutcome: LookupOutcome): string {
  if (live.state === "published") return live.label;
  if (live.state === "none") return "Not set";
  return lookupOutcome === "pending" ? LIVE_CHECKING : LIVE_UNREADABLE;
}

/**
 * The approved row that the selected record is a version OF.
 *
 * All three parts of the identity are required, and each one is load-bearing:
 *
 *   - `fact_key` — obviously.
 *   - `scope_id` — a company-wide key has one approved row per market, so
 *     picking the first approved row of the key would show a UK threshold
 *     beside a GCC draft.
 *   - `formula_version_id` — a per-product nutrition fact carries the WILDCARD
 *     scope, so every product's energy value shares one `scope_id`. Matching on
 *     key and scope alone would announce another product's 899 kcal as the live
 *     value behind this product's draft, which is the exact class of error this
 *     whole resolution exists to stop.
 *
 * `null` rather than a guess when nothing matches: "nothing is published" is a
 * claim the caller must be able to make deliberately.
 */
export function publishedAmong(items: Fact[], selected: Fact): Fact | null {
  return (
    items.find(
      (item) =>
        item.status === "approved" &&
        item.fact_key === selected.fact_key &&
        item.scope_id === selected.scope_id &&
        item.formula_version_id === selected.formula_version_id,
    ) ?? null
  );
}

/**
 * What customers see right now — a THREE-state answer, because "nothing is
 * published" and "we could not find out" are different claims and only one of
 * them means the storefront has a hole in it.
 *
 * An approved record is its own live value. Anything else — draft, superseded,
 * rejected — needs the approved sibling read first, and until that read settles
 * the pane says so rather than printing the selected record's number under a
 * "Live now" caption. A key with no record at all is the one case that reaches
 * "nothing is published" without a read.
 */
export type LiveValue =
  | { state: "published"; label: string }
  | { state: "none" }
  | { state: "unknown" };

/**
 * How the approved-sibling read has turned out.
 *
 * Four states, not a `settled` boolean, because "settled successfully" and
 * "settled by failing" carry opposite meanings here and a boolean invites the
 * caller to OR them together.
 *
 * Only `loaded` — a read that holds EVERY record for the key — licenses the
 * negative claim that nothing is published. `partial` is the read that came
 * back fine but was capped by the page limit: it can confirm an approved row
 * it saw, and it can prove nothing by not seeing one. `pending` and `failed`
 * mean the answer is not known at all.
 */
export type LookupOutcome = "pending" | "loaded" | "partial" | "failed";

export function liveValueFor({
  selected,
  published,
  lookupNeeded,
  lookupOutcome,
  unit,
}: {
  selected: Pick<Fact, "value" | "status"> | null;
  published: Pick<Fact, "value"> | null;
  lookupNeeded: boolean;
  lookupOutcome: LookupOutcome;
  unit: string | null;
}): LiveValue {
  // An approved record IS the live value. No second read can contradict it, so
  // this answer is reachable however the lookup turned out.
  if (selected !== null && selected.status === "approved") {
    return { state: "published", label: valueWithUnit(selected.value, unit) };
  }
  // No record at all — a registry key that has never been given a value. The
  // ONLY route to `none` that does not depend on a completed read, because it
  // is the one case where nothing could have been published.
  if (selected === null) return { state: "none" };
  // Anything else — draft, superseded, rejected — is not the live value, so the
  // approved sibling has to be read before a word is said about the storefront.
  //
  // `!lookupNeeded` lands here rather than on `none`. It used to short-circuit
  // to `none`, which made the claim "no value has ever been published" a
  // consequence of the CALLER having decided not to look. When the caller's
  // status list missed a case — it missed `rejected` — a rejected record sat
  // over a live approved sibling and reported the storefront as empty.
  //
  // Anything short of a completed read leaves the live value UNKNOWN. A failed
  // read must never fall through to `none` either: `none` is rendered as "No
  // value has ever been published for this" and as "Live now: Not set" in the
  // approve confirm, so a failed read would talk an operator into overwriting a
  // live number it simply could not see.
  if (!lookupNeeded || lookupOutcome === "pending" || lookupOutcome === "failed") {
    return { state: "unknown" };
  }
  if (published !== null) {
    return { state: "published", label: valueWithUnit(published.value, unit) };
  }
  // Nothing approved in what we read — which is only the same thing as
  // "nothing is published" when we read EVERYTHING. The sibling list is a
  // paged endpoint, and a key with more records than one page (a long-lived
  // per-product number accumulates a version per correction) had its approved
  // row fall off the end. `none` renders as "No value has ever been published
  // for this" and as "Live now: Not set" on the confirm, so a capped read was
  // talking the operator into overwriting a live number it never fetched.
  return lookupOutcome === "loaded" ? { state: "none" } : { state: "unknown" };
}

/**
 * The record whose reach the blast-radius line must count.
 *
 * It is the LIVE record, never the record the pane was opened on. On the "pick
 * up someone else's draft" path those are different rows: the draft is
 * published nowhere, so `GET /facts/{draft}/impact` truthfully answered zero
 * and the pane rendered "This is not published anywhere yet" over an approved
 * sibling sitting on thirteen panels. Approving then changed every one of them.
 * That is KET-3853 exactly — a correction published against a reach the
 * operator was told was empty — reintroduced by the one screen built to stop it.
 *
 * Derived FROM `liveValueFor`'s answer rather than from a second reading of the
 * statuses, so the count can never describe a record the pane did not call
 * live: no live record, no count. A `pending`, `failed` or capped sibling read
 * therefore yields `null` here, and the caller renders that as an unread reach
 * rather than as zero — a fabricated zero is the one wrong answer that reads as
 * reassurance.
 */
export function impactTargetFor({
  live,
  selected,
  published,
}: {
  live: LiveValue;
  selected: Pick<Fact, "fact_id" | "status"> | null;
  published: Pick<Fact, "fact_id"> | null;
}): string | null {
  if (live.state !== "published") return null;
  // The two routes `liveValueFor` takes to `published`, in its order: an
  // approved record is its own live value; anything else is live only through
  // the approved sibling that was read for it.
  if (selected !== null && selected.status === "approved") return selected.fact_id;
  return published?.fact_id ?? null;
}

/**
 * Whether a candidate value is a legal one for this row, in the operator's
 * words — `null` when it is, or there is nothing to judge yet.
 *
 * Exists so the question can be asked of a value that is NOT the one this
 * render was built from. The render derives `typeError` from its own
 * `nextValue`, which is correct for what is on screen and WRONG for a value
 * arriving inside a synchronous change-then-commit handler; see `commit`.
 */
export function typeErrorFor(
  row: FactRow,
  fact: Fact | null,
  value: string,
  kind: ValueKind,
): string | null {
  if (value === "") return null;
  const built = buildValue(row, fact, value, kind);
  return "error" in built ? built.error : null;
}

/**
 * The ONE envelope every shape question on this pane is asked about.
 *
 * The record the pane is showing, then the row's own copy of it, then `{}` for
 * a row with no record at all — NEVER a fabricated `{ n: 0 }` / `{ s: "" }`.
 * `holdsStructuredValue` consults the envelope's shape only when the registry
 * declared no type, and there an invented one is not neutral: it reads an `"s"`
 * key as proof of a scalar, so a key whose `value_type` never arrived was
 * answered "scalar" off a value the caller had just made up, and `{ s: input }`
 * went under an object-holding key. An empty envelope makes no claim, so an
 * undeclared shape falls to "neither n nor s" and is refused rather than
 * guessed.
 *
 * `valueKindOf` and `buildValue` both resolve through here so they cannot be
 * looking at two different values — the fetched record and the row's own copy
 * are the same fact, but only one of them is what the operator is reading.
 */
function envelopeOf(row: FactRow, fact: Fact | null): Record<string, unknown> {
  return (fact ?? row.fact)?.value ?? {};
}

/**
 * Build the replacement value in the SAME shape as the value being corrected,
 * or — for a key that has never had one — in the shape the registry declares.
 */
function buildValue(
  row: FactRow,
  fact: Fact | null,
  input: string,
  kind: ValueKind,
): { value: Record<string, unknown> } | { error: string } {
  // A compound value is assembled from the editor's ROWS, never parsed out of a
  // typed string. `input` here is the encoded row state, not something a person
  // wrote — see `nextValue` in the pane for why it travels this way.
  if (kind === "ingredients") return ingredientsToValue(decodeRows(input));
  return draftValueFor(envelopeOf(row, fact), input, row.key?.value_type);
}

/**
 * Which control authors this fact.
 *
 * Resolved from the value the fact actually holds first, and from the registry
 * key when there is no value yet — a Missing row has nothing to read a shape
 * off, and a first ingredient list must still get the row editor rather than a
 * text box that would save it as a string.
 *
 * `structured` is the honest fourth answer: an object-valued key this screen
 * has no editor for. It renders a stated refusal, NOT a JSON textarea. A JSON
 * box would put the store's internal encoding in front of a persona who has
 * never seen a fact key, and one stray brace publishes a malformed panel.
 */
export type ValueKind = "number" | "string" | "ingredients" | "structured";

/**
 * An ingredient envelope as one line per ingredient, for the diff card.
 *
 * An em dash between the name and the amount rather than a bare space: the two
 * halves of a row are a label and a quantity, and "Arabic Gum 8,345 mg" read at
 * speed down a column of them runs together.
 */
function asLines(value: Record<string, unknown> | null | undefined): string[] | null {
  const rows: Ingredient[] | null = ingredientsIn(value);
  if (rows === null) return null;
  return rows.map((row) =>
    row.amount_mg === null ? row.name : `${row.name} — ${milligrams(row.amount_mg)}`,
  );
}

export function valueKindOf(row: FactRow | undefined, fact: Fact | null): ValueKind {
  if (row === undefined) return "string";
  const record = fact ?? row.fact;
  // An ingredient list this pane can read AND hold in full gets the row editor.
  //
  // Only on an ingredient KEY, and only when the editor can hold every field
  // the value carries. Any `{ items: [{ name }] }` used to qualify, so another
  // compound fact of that shape got this editor, and a save rebuilt it from
  // name, amount and position alone — an allergen list or a note dropped with
  // no warning. Anything the editor cannot represent in full is shown, not
  // edited.
  if (record !== null && isIngredientKey(row) && ingredientsEditable(record.value)) {
    return "ingredients";
  }
  // The key's own attribute, for a row with NO RECORD behind it. The registry
  // declares `object` for every compound key but not WHICH object, so the
  // attribute is what names the one shape this pane can author.
  //
  // Only for a row with no record. Read off the key alone, it fired over a
  // stored value `ingredientsIn` had just refused: the editor seeded itself
  // from the `[]` that refusal falls back to, the pane said "No ingredients
  // yet." over a fact that held something, and the first save rebuilt the value
  // from those rows — discarding the malformed original with no warning and no
  // trace of what had been there. A value this screen cannot read is not an
  // empty list, and the one screen whose job is correcting customer-facing
  // numbers must not overwrite what it could not show.
  if (record === null && isIngredientKey(row)) return "ingredients";
  // The SAME predicate `draftValueFor`'s backstop uses, on the SAME envelope,
  // not a second spelling of it — see `holdsStructuredValue`. Asking separately
  // is how an untyped key with no record came to render an editable box that
  // the backstop then refused on every save.
  if (holdsStructuredValue(envelopeOf(row, fact), row.key?.value_type)) return "structured";
  return isNumeric(row, fact) ? "number" : "string";
}

/** A key whose compound shape IS the ingredient list this pane can author. */
export function isIngredientKey(row: FactRow): boolean {
  return row.key?.attribute === "active_ingredients" || row.factKey.endsWith(".active_ingredients");
}

function isNumeric(row: FactRow, fact: Fact | null): boolean {
  const declared = row.key?.value_type;
  if (declared) return /^(number|numeric|int|integer|float|decimal)$/i.test(declared);
  const record = fact ?? row.fact;
  return record !== null && "n" in record.value;
}

/**
 * Why Approve is not available, in the operator's words.
 *
 * Order matters: the value must be right before it can be saved, and it must be
 * saved before a source can approve it. Naming only the first blocker at a time
 * is what keeps this a next step rather than a list of complaints.
 */
export function approveBlockedBecause({
  typeError,
  hasTarget,
  hasValue,
  draftFailed,
  source,
  valueKind = "number",
}: {
  typeError: string | null;
  hasTarget: boolean;
  hasValue: boolean;
  /**
   * The last attempt to store the draft FAILED.
   *
   * Without it, "Saving your correction…" was the answer forever: a failed save
   * leaves no draft and a non-empty field, which is the same shape as a save
   * still in flight. The operator watched a spinner-shaped sentence that had
   * stopped being true, under a button that was never going to enable.
   */
  draftFailed: boolean;
  source: string;
  /**
   * Which control is on screen, so the first blocker names the right thing to
   * fix. "while it is not a number" under an ingredient list is a sentence
   * about a field the operator is not looking at.
   */
  valueKind?: ValueKind;
}): string | null {
  if (typeError !== null) {
    return valueKind === "ingredients"
      ? "Fix the ingredients first — nothing can be published while a row is incomplete."
      : "Fix the value first — nothing can be published while it is not a number.";
  }
  if (!hasTarget) {
    if (hasValue && draftFailed) {
      return "Your correction could not be saved — leave the field again to try once more.";
    }
    if (hasValue) return "Saving your correction… it can be published once the draft is stored.";
    // The ingredient editor has no "new value" to enter — it opens holding the
    // live list — so the sentence names the thing that is actually on screen.
    return valueKind === "ingredients"
      ? "Change an ingredient first — there is nothing to publish yet."
      : "Enter a new value first — there is nothing to publish yet.";
  }
  if (source.trim() === "") {
    return "Say what you checked this against — an approval with no source is not an approval.";
  }
  return null;
}

/** "Approved 2 Sep by Damien" — who stands behind the number that is live. */
function provenance(fact: Fact): string {
  if (fact.status === "draft") return "Drafted, not published. Customers still see the live value.";
  if (!fact.approved_at) return "Never approved.";
  const who = fact.approved_by ? ` by ${fact.approved_by}` : "";
  return `Approved ${exactDate(fact.approved_at)}${who} · ${ageOf(fact.approved_at)}`;
}

/**
 * The correction field: the label, the box, the unit beside it, and the one
 * line under it that is either the type error or what leaving the field will do.
 *
 * Extracted from the pane because the pane cannot be rendered into a WRONG-TYPE
 * state without a keyboard — `typeError` is derived from what was typed — and
 * the screenshot harness has no keyboard. A state that cannot be photographed
 * is a state nobody checks, and "the value is not the right kind" is one of the
 * two error states the whole approval path hangs on. Exported so the harness
 * renders THIS component with a bad value in it, rather than a hand-built
 * lookalike that would drift from the real one the moment either changed.
 */
export function ValueField({
  id,
  helpId,
  label,
  unit,
  numeric,
  value,
  error,
  help,
  onChange,
  onBlur,
}: {
  id: string;
  helpId: string;
  label: string;
  unit: string | null;
  numeric: boolean;
  value: string;
  /** The value is not the shape this fact holds. Shown INSTEAD of `help`. */
  error: string | null;
  help: string;
  onChange: (next: string) => void;
  onBlur: () => void;
}) {
  return (
    <div>
      <Label htmlFor={id}>{label}</Label>
      <div className="flex items-center gap-2">
        <Input
          id={id}
          className="h-9"
          // A hint, never the type rule: `draftValueFor` is what decides the
          // shape, and it refuses rather than coercing.
          inputMode={numeric ? "decimal" : "text"}
          aria-describedby={helpId}
          aria-invalid={error !== null}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          onBlur={onBlur}
        />
        {/* The unit is rendered as a suffix, never typed: "899 kcal" typed
            into a numeric field is not a number. */}
        {unit && <span className="text-sm text-ks-grey-1">{unit}</span>}
      </div>
      <p id={helpId} className="pt-1 text-sm text-ks-grey-1">
        {error ? (
          // Red, not red AND bold: the outline already marks the field, and the
          // mock-up sets the sentence at the weight of the help it replaces.
          <span role="alert" className="text-ks-danger">
            {error}
          </span>
        ) : (
          help
        )}
      </p>
    </div>
  );
}

/**
 * "What did you check this against?" — the box that turns an approval into a
 * retraceable one. Extracted alongside `ValueField` so a harness screen showing
 * one form state shows the whole form, from the real components.
 */
export function SourceField({
  id,
  helpId,
  value,
  onChange,
}: {
  id: string;
  helpId: string;
  value: string;
  onChange: (next: string) => void;
}) {
  return (
    <div>
      <Label htmlFor={id}>What did you check this against?</Label>
      <Textarea
        id={id}
        value={value}
        aria-required
        aria-describedby={helpId}
        onChange={(event) => onChange(event.target.value)}
        placeholder="R&D sheet · OUTPUT - Nutrition Info Table · row 14, Production Formula v7"
      />
      <p id={helpId} className="pt-1 text-sm text-ks-grey-1">
        Required. The source is stored with the approval, so anyone can retrace this number later.
      </p>
    </div>
  );
}

/**
 * The one primary action, and the reason it is unavailable when it is.
 *
 * Full width at 44px, as the approved mock-up draws it: the button is the end
 * of the pane's single column, and a 32px control floating at the left of a
 * 460px pane read as one more secondary thing rather than as the act of
 * publishing to every storefront.
 */
export function ApproveAction({
  label = "Approve and publish",
  onClick,
  disabled,
  blocked,
}: {
  label?: string;
  onClick: () => void;
  disabled: boolean;
  /** Why it is unavailable, in the operator's words — `null` when it is not. */
  blocked: string | null;
}) {
  return (
    <div>
      {/* Disabled is GREY, not a faded purple. shadcn's base drops a disabled
          button to `opacity-50`, which on the primary leaves a washed-out
          purple that still reads as the call to action — the eye finds it and
          the pointer goes to it before the reason underneath is read. The
          approved mock-up draws the unavailable state as a flat grey fill with
          grey text (`PaneStates.dc.html:51`), and both of its values are brand
          tokens already — the fill is `--ks-grey-4` and the text `--ks-grey-1`,
          exactly. So this is the mock's own treatment written in the portal's
          palette, not an approximation of it.
          Full opacity is deliberate: the greyness is what says unavailable, so
          fading it as well would only make the grey text harder to read. */}
      <Button
        className="h-11 w-full text-[15px] font-bold disabled:bg-ks-grey-4 disabled:text-ks-grey-1 disabled:opacity-100"
        onClick={onClick}
        disabled={disabled}
      >
        {label}
      </Button>
      {/* A disabled button never sits there unexplained — the reason is stated
          where the eye already is. */}
      {blocked && <p className="pt-2 text-sm text-ks-grey-1">{blocked}</p>}
    </div>
  );
}

/**
 * Which fact the pane is about: product line, title, machine key and scope.
 *
 * Exported so the harness's composed screens — a correction in progress, the
 * mobile sheet — carry the same identity the real pane opens with. They were
 * photographed without it, so the pair showed a diff card for no named fact
 * beside a mock-up that names it.
 */
export function FactHeader({
  row,
  fact: selectedFact,
  companyWide,
}: {
  row: FactRow;
  fact: Fact | null;
  companyWide: boolean;
}) {
  return (
    <header className="space-y-1">
      {/* A nutrition number is meaningless without the product it describes.
          A company-wide fact HAS no product, so it says so in grey rather
          than leaving a blank — a blank would read as a lookup that failed,
          and "Every product" is the true answer, not a missing one. The tone
          is the difference: purple names a specific product, grey names the
          absence of one, so the two never read as the same kind of line. */}
      {companyWide ? (
        <p className="text-sm font-bold text-ks-grey-1">Every product</p>
      ) : (
        <p className="text-sm font-bold text-ks-purple">
          {selectedFact ? productLabel(selectedFact) : row.product}
        </p>
      )}
      <h3 className="text-lg leading-snug font-bold break-words text-ks-ink">{row.title}</h3>
      {/* 11px with its own line-height: the machine key inherits nothing from
          the title above it, and a 1.25rem leading on an 11px string opens a
          gap that makes the key look like a separate block rather than the
          title's second line. */}
      <p className="font-mono text-[11px] leading-4 break-all text-ks-grey-1">{row.factKey}</p>
      {/* The registry description, now that the title is the fact's short
          name (KET-6326). Read from `/v1/fact-keys` — the fact routes carry no
          description. Absent while a fact has no name: the description is the
          title then, and a second copy of it under itself says nothing. */}
      {row.definition && (
        <p className="pt-1 text-[13px] leading-snug text-ks-grey-1">
          <span className="font-bold text-ks-ink">Definition</span> {row.definition}
        </p>
      )}
      {/* The scope as chips, one per dimension, rather than one dot-joined
          string. The string form is right for a table cell and wrong here:
          the pane has the room, and "All markets · All stores" asks the
          reader to work out where one fact ends and the next begins.

          A company-wide key is scoped per MARKET and every one of its markets
          is listed below by `MarketStack`, so a chip row here would state the
          scope twice — once as a pill, once as a table. */}
      {!companyWide && (
        <div className="flex flex-wrap gap-1.5 pt-1.5">
          {(selectedFact ? scopeChips(selectedFact.scope) : [row.market]).map((chip) => (
            <span
              key={chip}
              className="inline-flex h-[22px] items-center rounded-full bg-ks-grey-4 px-2 text-xs text-ks-ink"
            >
              {chip}
            </span>
          ))}
        </div>
      )}
    </header>
  );
}

/**
 * Someone approved a different correction first (409).
 *
 * Exported for the same reason as `ValueField`: the pane reaches this state
 * only by a rejected request, which a static render cannot produce.
 */
export function ConflictCard({
  typed,
  onReload,
}: {
  typed: string | null;
  onReload: () => void;
}) {
  /* This card writes its own sentence rather than borrowing the shared
     `messageFor(409)` one, which is written for a caller that has no button:
     it said "a different DRAFT first" under a title that says "a different
     CORRECTION first" — one card naming the same event two ways — and then
     "Reload to see the current value" directly above a Reload button.

     It is drawn as the shared `AlertCard` — circle mark, ink sentence, grey
     reassurance — with Reload as its own purple-outline button UNDER the card,
     as the mock-up sets it. It was a destructive Alert: every line red, no
     mark, and the button run inline after "nothing was lost", which is the
     treatment G3 took off the S1 load-failure card (outcome-audit cycle 3, M3).

     What it deliberately does NOT say is the thing the mock-up's card says:
     the value that won and who approved it ("Energy per 100 g is now 902
     kcal"). A 409 body COULD carry the winning fact — `ProtexError` keeps one —
     but nothing in this module is handed it, and the endpoint's 409 shape is
     not this branch's to assert. Declared. */
  return (
    <div className="space-y-3">
      <AlertCard
        icon="circle"
        title="Someone approved a different correction first. The live value has moved since you opened this."
        detail={typed ? <>Your {typed} is still in the box below — nothing was lost.</> : null}
      />
      <Button
        variant="outline"
        size="sm"
        className="border-ks-purple bg-ks-surface font-bold text-ks-purple hover:bg-ks-purple-4 hover:text-ks-purple"
        data-nonmutating
        onClick={onReload}
      >
        Reload this fact
      </Button>
    </div>
  );
}

/**
 * The markets of one company-wide key, side by side.
 *
 * `shipping.free_threshold_value` is £45 UK, €50 EU and AED 200 GCC — one key,
 * one approved row per scope. Each is corrected on its own, with its own
 * approval and its own source.
 */
function MarketStack({
  row,
  query,
  areaName,
}: {
  row: FactRow;
  query: { data?: { data: { items: Fact[] } }; isLoading: boolean; isError: boolean; error: unknown };
  areaName: string;
}) {
  if (query.isLoading) return <Skeleton className="h-16 w-full" />;
  if (query.isError) {
    return (
      <p role="alert" className="text-sm text-ks-danger">
        {messageFor(query.error, areaName)}
      </p>
    );
  }

  const rows = (query.data?.data.items ?? []).filter((fact) => fact.status === "approved");
  if (rows.length === 0) return null;

  return (
    <section className="space-y-1">
      <h4 className="sr-only">Every market for this fact</h4>
      {/* A fixed market column, the value growing beside it, the age at the far
          right — the same three-part row the mock-up draws. The old layout was
          `justify-between` on three spans, which let the market column's width
          follow the length of its own label: "UK" and "GCC · Amazon" started in
          different places, so the column of values behind them did too, and a
          list whose whole job is to be compared row against row could not be. */}
      <ul className="text-sm">
        {rows.map((fact) => (
          <li
            key={fact.fact_id}
            className="flex items-center gap-3 border-b border-ks-grey-4 py-2.5 last:border-b-0"
          >
            <span className="w-[90px] shrink-0 text-ks-grey-1">{marketLabel(fact.scope)}</span>
            <span className="grow text-base font-bold text-ks-ink">
              {valueWithUnit(fact.value, fact.unit ?? row.key?.unit ?? null)}
            </span>
            <span className="text-xs text-ks-grey-1">
              {fact.approved_at ? `approved ${ageOf(fact.approved_at)}` : "never published"}
            </span>
          </li>
        ))}
      </ul>
      {/* Why there is no product line above, said once, where the absence is.
          A reader who has only ever opened per-product facts arrives here with
          a missing product line and no way to tell a company-wide fact from a
          lookup that failed. */}
      <p className="pt-2 text-xs leading-snug text-ks-grey-1">
        One market is corrected at a time — each has its own approval and its own source. There is
        no product line here, because there is no product: this fact is true for all of them.
      </p>
    </section>
  );
}

function History({
  areaName,
  query,
  fact,
  unit,
  enabled,
}: {
  areaName: string;
  query: {
    data?: { data: Array<{
      event_id: string;
      action: "approve" | "reject" | "supersede";
      actor: string;
      self_approved: boolean;
      prior_value: Record<string, unknown> | null;
      source_locator: string | null;
      created_at: string;
    }> };
    isLoading: boolean;
    isError: boolean;
    error: unknown;
  };
  /** The record whose history this is — its value is what its newest approval published. */
  fact: { value: Record<string, unknown>; status: string } | null;
  unit: string | null;
  enabled: boolean;
}) {
  if (!enabled) return null;

  const events = query.data?.data ?? [];
  // The mock-up's line is "Damien approved 899 kcal (was 900 kcal)": what was
  // approved is the answer to "where did this number come from", and the line
  // that only said what it REPLACED made the reader work the chain out.
  const approved = approvedValuesFor(events, fact);
  const verbs = { approve: "approved", reject: "rejected", supersede: "superseded" } as const;

  return (
    <section className="space-y-2">
      <h4 className="text-sm font-bold text-ks-ink">History</h4>
      {query.isLoading && <Skeleton className="h-12 w-full" />}
      {/* "No events" and "we could not load events" are different claims. */}
      {query.isError && (
        <p role="alert" className="text-sm text-ks-danger">
          {messageFor(query.error, areaName)}
        </p>
      )}
      {!query.isLoading && !query.isError && events.length === 0 && (
        <p className="text-sm text-ks-ink">No events yet.</p>
      )}
      <ul className="space-y-2.5">
        {/* NEWEST FIRST, sorted by the timestamp rather than trusted from the
            order the server happened to return. `.reverse()` was flipping an
            already-newest-first payload, so the pane opened on a value approved
            in May and the reader had to scroll to find what is live today. The
            first line of a history is the answer to "what happened last". */}
        {[...events]
          .sort((a, b) => b.created_at.localeCompare(a.created_at))
          .map((event) => {
            const value = approved.get(event.event_id);
            return (
              <li key={event.event_id} className="space-y-0.5">
                <p className="text-sm leading-[1.5] text-ks-ink">
                  {/* The timestamp is bold, as the mock-up sets it: in a trail
                      the when is what the eye walks down. */}
                  <time dateTime={event.created_at} className="font-bold">
                    {exactDate(event.created_at)}
                  </time>
                  {" — "}
                  {event.actor} {verbs[event.action] ?? event.action}
                  {value ? (
                    <>
                      {" "}
                      {valueWithUnit(value, unit)}
                      {event.prior_value && (
                        <span className="text-ks-grey-1">
                          {" "}
                          (was {valueWithUnit(event.prior_value, unit)})
                        </span>
                      )}
                    </>
                  ) : event.prior_value ? (
                    <span className="text-ks-grey-1">
                      {" "}
                      (was {valueWithUnit(event.prior_value, unit)})
                    </span>
                  ) : (
                    // "First value", never a blank — with no number on the
                    // line, a blank would read as "nothing changed".
                    <span className="text-ks-grey-1"> (first value)</span>
                  )}
                  {/* Self-approval proceeds and is LABELLED, never blocked —
                      blocking it would strand a single-author domain unable to
                      publish. Inside the line, as the mock-up draws it, so it
                      qualifies this approval rather than reading as a second
                      claim. */}
                  {event.self_approved && (
                    <span className="ml-1.5 inline-flex h-5 items-center rounded-full bg-ks-grey-4 px-[7px] align-middle text-[11px] text-ks-ink">
                      Self-approved
                    </span>
                  )}
                </p>
                {event.source_locator ? (
                  <p className="text-[13px] leading-[1.5] text-ks-grey-1">
                    Checked against: {event.source_locator}
                  </p>
                ) : (
                  <p className="text-[13px] leading-[1.5] text-ks-danger">No source recorded</p>
                )}
              </li>
            );
          })}
      </ul>
    </section>
  );
}

/**
 * The confirm step, shown before anything publishes.
 *
 * It restates old → new with units, the source, and how far it reaches. This is
 * the last place the operator can notice they corrected the wrong number, and it
 * is the reason the primary button is a two-step action rather than one.
 */
function ApproveDialog({
  container,
  open,
  onOpenChange,
  title,
  product,
  market,
  unit,
  currentLabel,
  nextLabel,
  currentLines = null,
  nextLines = null,
  source,
  impact,
  impactPending,
  impactFailed,
  impactFirstValue,
  selfApproved,
  pending,
  onConfirm,
}: {
  /**
   * Where the dialog's portal mounts. Required, not optional: the body default
   * would put this dialog outside the shell's permission gate, and a publish
   * button that escapes the gate is the whole reason the prop is threaded here.
   */
  container: HTMLElement;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  product: string;
  market: string;
  /** Set apart from the figure in the band, exactly as the pane sets it. */
  unit: string | null;
  currentLabel: string;
  nextLabel: string;
  /** A compound value's parts, one per line — see `ValueDiff`. */
  currentLines?: string[] | null;
  nextLines?: string[] | null;
  source: string;
  impact: Parameters<typeof BlastRadiusLine>[0]["impact"];
  impactPending: boolean;
  impactFailed: boolean;
  impactFirstValue: boolean;
  selfApproved: boolean;
  pending: boolean;
  onConfirm: () => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent container={container} className="gap-[18px] sm:max-w-[640px]">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-ks-ink">
            Publish this correction?
          </DialogTitle>
          {/* The identity is drawn INSIDE the band below, beside the numbers it
              belongs to — the mock-up sets it there rather than as a subtitle,
              because "which fact is this" and "what is changing" are one glance.
              It stays here for the accessible description Radix names the dialog
              by: a dialog whose only description is visual has none. */}
          <DialogDescription className="sr-only">
            {product} · {title} · {market}
          </DialogDescription>
        </DialogHeader>

        <ValueDiff
          variant="confirm"
          unit={unit}
          current={currentLabel}
          next={nextLabel}
          currentLines={currentLines}
          nextLines={nextLines}
          currentCaption="Live now"
          nextCaption="Will become"
          trailing={
            <>
              <span className="font-bold">{product}</span>
              <br />
              {title}
              <br />
              <span className="text-ks-grey-1">{market}</span>
            </>
          }
        />

        {/* Reach, source and who approved it, as one icon-led list: three
            answers to three different questions, each marked so the eye can
            find the one it came for. */}
        <div className="space-y-2.5">
          <BlastRadiusLine
            variant="note"
            impact={impact}
            pending={impactPending}
            failed={impactFailed}
            firstValue={impactFirstValue}
          />

          <ConfirmNote icon={<FileText aria-hidden className="size-[18px] text-ks-grey-1" />}>
            Checked against: <span className="text-ks-grey-1">{source}</span>
          </ConfirmNote>

          {selfApproved && (
            <ConfirmNote icon={<UserRound aria-hidden className="size-[18px] text-ks-grey-1" />}>
              You drafted this and you are approving it —{" "}
              <span className="inline-flex h-5 items-center rounded-full bg-ks-grey-4 px-[7px] text-[11px] text-ks-ink">
                Self-approved
              </span>
              <span className="text-ks-grey-1"> will be recorded on the audit trail.</span>
            </ConfirmNote>
          )}
        </div>

        <DialogFooter>
          {/* Backing out publishes nothing, so it is not an edit control —
              without the marker, the gate would leave a reader who reached this
              dialog with no way out of it. */}
          <Button
            variant="outline"
            className="h-10 px-[18px]"
            data-nonmutating
            onClick={() => onOpenChange(false)}
          >
            Keep editing
          </Button>
          <Button className="h-10 px-[22px] font-bold" onClick={onConfirm} disabled={pending}>
            {pending ? "Publishing…" : "Publish this correction"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * One marked line in the confirm dialog — an icon, then the sentence.
 *
 * The icon is what makes three consecutive sentences readable as three separate
 * answers rather than as a paragraph: reach, source, and who is approving are
 * different questions, and a reader who came for one of them should not have to
 * read the other two to find it.
 *
 * Exported for the screenshot harness, on the same terms as `ValueField` and
 * `ConflictCard`: the dialog itself mounts through a Radix PORTAL, which the
 * static renderer cannot follow, so the confirm screen is composed from the
 * dialog's own parts rather than from a lookalike that would drift from them.
 */
export function ConfirmNote({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <div className="flex items-start gap-2.5">
      <span className="mt-0.5 shrink-0">{icon}</span>
      <p className="text-sm leading-[1.5] text-ks-ink">{children}</p>
    </div>
  );
}

export { formatValue };
